/**
 * SalariesTab — Menu Salaires Pro (Gestion RH & Paie Espace Maxo)
 *
 * Dashboard RH :
 *   - 6 KPI (actifs, charge mensuelle, moyenne, signés/mois, à signer, bons crédit)
 *   - Toolbar : recherche + tri + filtre statut
 *   - Cartes employé enrichies (matricule, contrat, ancienneté, badges fiscaux)
 *   - Formulaire multi-sections (Identité / Contrat / Contact / Paie / Fiscal)
 *   - Case à cocher CNSS / ITS optionnels par employé
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, X, Check, Users, TrendingUp, Loader2, RefreshCw,
  FileText, Search, ShieldCheck, ShieldAlert, ArrowUpDown, UserPlus,
  Wallet, ClipboardCheck, ClipboardList, Briefcase, Phone, Mail, MapPin,
  Calendar as CalIcon, IdCard, Landmark, FileDown, Printer,
} from "lucide-react";
import PayslipModal from "./PayslipModal";
import InternsTable from "./InternsTable";
import SalaryHistoryPanel from "./SalaryHistoryPanel";
import WeeklyScheduleTab from "./WeeklyScheduleTab";
import SalaryBreakdownTable from "./SalaryBreakdownTable";
import PaymentSchedulePanel from "./PaymentSchedulePanel";
import { printMonthlyPayslipsPDF } from "../utils/salaryPdfHelpers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (Math.round(v || 0)).toLocaleString('fr-FR');
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const CONTRACT_TYPES = ["CDI", "CDD", "Stage", "Journalier"];

/**
 * Génère un PDF annuel pour UN employé (ou pour tous si multiple = true).
 * Interroge /api/salaries/{id}/payslip/{year}/{month} pour les 12 mois de l'année.
 */
async function generateAnnualPayslipsPDF({ employees = [], year, title }) {
  if (!employees || employees.length === 0) return;
  // 🔑 IMPORTANT : ouvrir la fenêtre AVANT les await (sinon les navigateurs bloquent
  // comme pop-up non sollicité). On y met un splash pendant le chargement.
  const w = window.open("", "_blank");
  if (!w) { toast.error("Le navigateur a bloqué l'ouverture — autorisez les pop-ups pour ce site"); return; }
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title || `Salaires ${year}`}</title>
<style>body{font-family:system-ui,sans-serif;background:#0f172a;color:white;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;text-align:center}
.sp{width:44px;height:44px;border:4px solid #334155;border-top-color:#10b981;border-radius:50%;animation:s 1s linear infinite;margin:0 auto 16px}
@keyframes s{to{transform:rotate(360deg)}}</style></head>
<body><div><div class="sp"></div><p>Génération du PDF…</p><p style="opacity:0.5;font-size:12px">Chargement des fiches de paie de ${employees.length} employé(s) × 12 mois</p></div></body></html>`);
  w.document.close();

  const rows = [];
  for (const emp of employees) {
    const monthlyData = [];
    // Charge les 12 mois en parallèle pour être plus rapide
    const promises = Array.from({ length: 12 }, (_, i) =>
      axios.get(`${API}/salaries/${emp.id}/payslip/${year}/${i + 1}`)
        .then(r => r.data)
        .catch(() => null)
    );
    const results = await Promise.all(promises);
    monthlyData.push(...results);
    rows.push({ employee: emp, monthly: monthlyData });
  }

  const employeesHTML = rows.map(({ employee: e, monthly }) => {
    const totalAnnual = monthly.reduce((s, p) => s + (p?.net_to_pay || 0), 0);
    const totalGross = monthly.reduce((s, p) => s + (p?.gains_total || p?.gross || 0), 0);
    const totalCNSS = monthly.reduce((s, p) => s + (p?.cnss_employee || 0), 0);
    const totalITS = monthly.reduce((s, p) => s + (p?.its_amount || 0), 0);
    const monthsPaid = monthly.filter(p => p && p.net_to_pay > 0).length;

    const monthRows = monthly.map((p, i) => {
      if (!p) return `<tr><td style="padding:4px 6px;font-size:10px">${MONTHS[i]}</td><td colspan="6" style="padding:4px 6px;font-size:10px;color:#94a3b8;font-style:italic;text-align:center">— aucune fiche —</td></tr>`;
      const signed = p.signed && !p.tampered ? "✓" : (p.tampered ? "⚠" : "—");
      return `
        <tr style="border-bottom:1px solid #e2e8f0">
          <td style="padding:4px 6px;font-size:10.5px;font-weight:600">${MONTHS[i]}</td>
          <td style="padding:4px 6px;font-size:10.5px;text-align:center">${p.days_paid ?? 0}</td>
          <td style="padding:4px 6px;font-size:10.5px;text-align:right;font-family:monospace">${fmt(p.gains_total || p.gross || 0)}</td>
          <td style="padding:4px 6px;font-size:10.5px;text-align:right;font-family:monospace;color:#991b1b">${fmt(p.cnss_employee || 0)}</td>
          <td style="padding:4px 6px;font-size:10.5px;text-align:right;font-family:monospace;color:#991b1b">${fmt(p.its_amount || 0)}</td>
          <td style="padding:4px 6px;font-size:10.5px;text-align:right;font-family:monospace;color:#065f46;font-weight:700">${fmt(p.net_to_pay || 0)}</td>
          <td style="padding:4px 6px;font-size:10.5px;text-align:center;color:${p.tampered ? '#dc2626' : (p.signed ? '#059669' : '#94a3b8')}">${signed}</td>
        </tr>`;
    }).join("");

    return `
      <section style="page-break-inside:avoid;margin-bottom:24px;border:1px solid #cbd5e1;border-radius:6px;overflow:hidden">
        <header style="background:#1F3661;color:white;padding:10px 12px">
          <div style="display:flex;justify-content:space-between;align-items:center">
            <div>
              <h2 style="margin:0;font-size:15px;letter-spacing:-0.01em">${e.name || "—"}</h2>
              <p style="margin:2px 0 0;font-size:10.5px;opacity:0.85">
                ${e.role_title || "—"} · ${e.matricule || "—"} · ${e.contract_type || "CDI"}
              </p>
            </div>
            <div style="text-align:right;font-size:10.5px">
              <p style="margin:0">Base : <b>${fmt(e.monthly_amount || 0)} F</b></p>
              <p style="margin:0;opacity:0.85">Année ${year}</p>
            </div>
          </div>
        </header>
        <table style="width:100%;border-collapse:collapse">
          <thead>
            <tr style="background:#f1f5f9">
              <td style="padding:6px;font-size:9.5px;text-transform:uppercase;letter-spacing:0.08em;font-weight:700;color:#475569;width:70px">Mois</td>
              <td style="padding:6px;font-size:9.5px;text-transform:uppercase;letter-spacing:0.08em;font-weight:700;color:#475569;text-align:center;width:40px">Jours</td>
              <td style="padding:6px;font-size:9.5px;text-transform:uppercase;letter-spacing:0.08em;font-weight:700;color:#475569;text-align:right">Brut (F)</td>
              <td style="padding:6px;font-size:9.5px;text-transform:uppercase;letter-spacing:0.08em;font-weight:700;color:#475569;text-align:right">CNSS</td>
              <td style="padding:6px;font-size:9.5px;text-transform:uppercase;letter-spacing:0.08em;font-weight:700;color:#475569;text-align:right">ITS</td>
              <td style="padding:6px;font-size:9.5px;text-transform:uppercase;letter-spacing:0.08em;font-weight:700;color:#475569;text-align:right">Net payé</td>
              <td style="padding:6px;font-size:9.5px;text-transform:uppercase;letter-spacing:0.08em;font-weight:700;color:#475569;text-align:center;width:40px">Signé</td>
            </tr>
          </thead>
          <tbody>${monthRows}</tbody>
          <tfoot>
            <tr style="background:#ecfdf5;border-top:2px solid #065f46">
              <td style="padding:7px;font-size:11px;font-weight:800;color:#065f46">TOTAL ${year}</td>
              <td style="padding:7px;font-size:10px;text-align:center;color:#64748b">${monthsPaid} mois</td>
              <td style="padding:7px;font-size:11px;text-align:right;font-family:monospace;font-weight:700">${fmt(totalGross)}</td>
              <td style="padding:7px;font-size:11px;text-align:right;font-family:monospace;font-weight:700;color:#991b1b">${fmt(totalCNSS)}</td>
              <td style="padding:7px;font-size:11px;text-align:right;font-family:monospace;font-weight:700;color:#991b1b">${fmt(totalITS)}</td>
              <td style="padding:7px;font-size:12px;text-align:right;font-family:monospace;font-weight:900;color:#065f46">${fmt(totalAnnual)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </section>`;
  }).join("");

  const grandTotal = rows.reduce((s, r) => s + r.monthly.reduce((sm, p) => sm + (p?.net_to_pay || 0), 0), 0);
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title || `Salaires ${year}`}</title>
<style>
  @page { size: A4; margin: 12mm; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color:#0f172a; margin:0; }
  header.main { padding:6px 0 12px; border-bottom:2px solid #0f172a; margin-bottom:12px; display:flex;justify-content:space-between;align-items:center }
  header.main h1 { margin:0;font-size:20px;letter-spacing:-0.02em }
  header.main .sub { color:#64748b;font-size:11px;margin-top:2px }
</style></head><body>
  <header class="main">
    <div>
      <h1>ESPACE MAXO — ${title || "État salarial annuel"}</h1>
      <div class="sub">Année ${year} · ${rows.length} employé(s) · Édité le ${new Date().toLocaleString("fr-FR")}</div>
    </div>
    <div style="text-align:right;font-size:11px;color:#64748b">
      Total net payé<br/>
      <b style="color:#065f46;font-size:16px;font-family:monospace">${fmt(grandTotal)} F CFA</b>
    </div>
  </header>
  ${employeesHTML}
  <p style="margin-top:16px;font-size:9px;color:#94a3b8;text-align:center">
    Espace Maxo SARL · Fijrosse, Cotonou · IFU 3202214184317 · RCCM RB/COT/22 B 32037<br/>
    Document confidentiel généré depuis Caisse Pro
  </p>
</body></html>`;

  // Utilise la fenêtre déjà ouverte (avant les await) — évite le blocage pop-up
  try {
    w.document.open();
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 500);
  } catch (e) {
    toast.error("Impossible de générer le PDF : " + e.message);
  }
}

const emptyForm = () => ({
  // Identité
  name: "",
  matricule: "",
  role_title: "",
  date_of_birth: "",
  cni: "",
  marital_status: "",
  children_count: 0,
  // Contrat
  contract_type: "CDI",
  start_date: "",
  end_date: "",
  contract_end_date: "",
  work_days_per_month: 26,
  leave_balance_days: 0,
  // Contact
  phone: "",
  email: "",
  address: "",
  mobile_money: "",
  // Paie
  monthly_amount: "",
  transport_allowance: 0,
  // Fiscal & social
  cnss_number: "",
  subject_to_cnss: false,
  subject_to_its: false,
  // Meta
  notes: "",
});

function seniority(startISO) {
  if (!startISO) return null;
  try {
    const start = new Date(startISO);
    const diff = Date.now() - start.getTime();
    const years = diff / (365.25 * 86400 * 1000);
    if (years < 1) return `${Math.floor(years * 12)} mois`;
    return `${years.toFixed(1)} ans`;
  } catch { return null; }
}

export default function SalariesTab({ currentUser }) {
  const isAdmin = currentUser?.role === "admin";
  const [items, setItems] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [payslipFor, setPayslipFor] = useState(null);
  const [subTab, setSubTab] = useState("permanent"); // permanent | interns
  // Toolbar
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");   // name | salary | seniority | contract
  const [statusFilter, setStatusFilter] = useState("active"); // active | archived | all
  // 🆕 Export annuel PDF
  const [pdfYear, setPdfYear] = useState(new Date().getFullYear());
  const [generatingPDF, setGeneratingPDF] = useState(false);
  // 🆕 Sélecteur mois/année pour dashboard KPI
  const [kpiYear, setKpiYear] = useState(new Date().getFullYear());
  const [kpiMonth, setKpiMonth] = useState(new Date().getMonth() + 1);

  const load = async () => {
    setLoading(true);
    try {
      const [list, db] = await Promise.all([
        axios.get(`${API}/salaries`),
        axios.get(`${API}/salaries/dashboard`, { params: { year: kpiYear, month: kpiMonth } }),
      ]);
      setItems(list.data || []);
      setDashboard(db.data || null);
    } catch (e) {
      toast.error("Erreur chargement : " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kpiYear, kpiMonth]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nom obligatoire"); return; }
    const amt = parseFloat(form.monthly_amount);
    if (!Number.isFinite(amt) || amt < 0) { toast.error("Salaire mensuel invalide"); return; }
    try {
      const payload = {
        name: form.name.trim(),
        role_title: form.role_title.trim(),
        matricule: form.matricule.trim(),
        monthly_amount: amt,
        work_days_per_month: parseInt(form.work_days_per_month) || 26,
        leave_balance_days: parseFloat(form.leave_balance_days) || 0,
        notes: form.notes.trim(),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        contract_end_date: form.contract_end_date || null,
        date_of_birth: form.date_of_birth || null,
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        cni: form.cni.trim(),
        contract_type: form.contract_type || "CDI",
        mobile_money: form.mobile_money.trim(),
        transport_allowance: parseFloat(form.transport_allowance) || 0,
        cnss_number: form.cnss_number.trim(),
        subject_to_cnss: !!form.subject_to_cnss,
        subject_to_its: !!form.subject_to_its,
        marital_status: form.marital_status || "",
        children_count: parseInt(form.children_count) || 0,
      };
      if (editingId) {
        await axios.patch(`${API}/salaries/${editingId}`, payload);
        toast.success("Employé mis à jour");
      } else {
        await axios.post(`${API}/salaries`, payload);
        toast.success("Employé ajouté");
      }
      setForm(emptyForm()); setShowAdd(false); setEditingId(null);
      load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const startEdit = (row) => {
    setForm({
      name: row.name || "",
      matricule: row.matricule || "",
      role_title: row.role_title || "",
      date_of_birth: row.date_of_birth || "",
      cni: row.cni || "",
      marital_status: row.marital_status || "",
      children_count: row.children_count || 0,
      contract_type: row.contract_type || "CDI",
      start_date: row.start_date || "",
      end_date: row.end_date || "",
      contract_end_date: row.contract_end_date || "",
      work_days_per_month: row.work_days_per_month || 26,
      leave_balance_days: row.leave_balance_days || 0,
      phone: row.phone || "",
      email: row.email || "",
      address: row.address || "",
      mobile_money: row.mobile_money || "",
      monthly_amount: String(row.monthly_amount || ""),
      transport_allowance: row.transport_allowance || 0,
      cnss_number: row.cnss_number || "",
      subject_to_cnss: !!row.subject_to_cnss,
      subject_to_its: !!row.subject_to_its,
      notes: row.notes || "",
    });
    setEditingId(row.id);
    setShowAdd(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(`${API}/salaries/${row.id}`, { active: !row.active });
      toast.success(row.active ? "Employé archivé" : "Employé réactivé");
      load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const removeItem = async (row) => {
    if (!window.confirm(`Supprimer définitivement "${row.name}" ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/salaries/${row.id}`);
      toast.success("Employé supprimé");
      load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  // 🆕 Export PDF annuel d'un employé (12 mois en un doc)
  const printAnnualPerEmployee = async (row) => {
    setGeneratingPDF(true);
    try {
      await generateAnnualPayslipsPDF({
        employees: [row],
        year: pdfYear,
        title: `Récapitulatif Salaires ${pdfYear} — ${row.name}`,
      });
    } finally {
      setGeneratingPDF(false);
    }
  };

  // 🆕 Export PDF annuel global (tous les employés actifs, année complète)
  const printAnnualGlobal = async () => {
    const targets = filtered;
    if (targets.length === 0) { toast.error("Aucun employé à exporter"); return; }
    if (!window.confirm(
      `Générer le PDF annuel pour ${targets.length} employé(s) sur l'année ${pdfYear} ?\n` +
      `Cela peut prendre quelques secondes (chargement des 12 fiches × ${targets.length} employés).`
    )) return;
    setGeneratingPDF(true);
    try {
      await generateAnnualPayslipsPDF({
        employees: targets,
        year: pdfYear,
        title: `État salarial annuel ${pdfYear}`,
      });
    } finally {
      setGeneratingPDF(false);
    }
  };

  // 🆕 Export PDF de TOUS les bulletins du mois sélectionné (1 clic)
  const printMonthlyAll = async () => {
    if (filtered.length === 0) { toast.error("Aucun employé à exporter"); return; }
    setGeneratingPDF(true);
    try {
      await printMonthlyPayslipsPDF({ employees: filtered, year: kpiYear, month: kpiMonth });
    } finally {
      setGeneratingPDF(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = items;
    if (statusFilter === "active") list = list.filter(i => i.active);
    else if (statusFilter === "archived") list = list.filter(i => !i.active);
    if (q) {
      list = list.filter(i =>
        (i.name || "").toLowerCase().includes(q) ||
        (i.matricule || "").toLowerCase().includes(q) ||
        (i.role_title || "").toLowerCase().includes(q) ||
        (i.phone || "").toLowerCase().includes(q)
      );
    }
    const arr = [...list];
    arr.sort((a, b) => {
      switch (sortBy) {
        case "salary": return (b.monthly_amount || 0) - (a.monthly_amount || 0);
        case "seniority": {
          const da = a.start_date ? new Date(a.start_date).getTime() : Infinity;
          const dbb = b.start_date ? new Date(b.start_date).getTime() : Infinity;
          return da - dbb;
        }
        case "contract":
          return (a.contract_type || "").localeCompare(b.contract_type || "");
        case "name":
        default:
          return (a.name || "").localeCompare(b.name || "");
      }
    });
    return arr;
  }, [items, search, sortBy, statusFilter]);

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-slate-400">
        Cette section est réservée à l&apos;administrateur.
      </div>
    );
  }

  return (
    <div className="sal-root space-y-3" data-testid="salaries-tab">
      <style>{`
        .sal-root, .sal-root * { font-family: 'Space Grotesk', 'Outfit', system-ui, sans-serif; }
        .sal-title { font-size: 17px; font-weight: 800; letter-spacing: -0.01em; color: white; }
        @media (min-width: 640px) { .sal-title { font-size: 20px; } }
        .sal-kpi {
          display: flex; align-items: center; gap: 10px;
          padding: 11px 12px; border-radius: 12px;
          background: rgba(30, 41, 59, 0.5);
          border: 1px solid rgba(71, 85, 105, 0.4);
          border-left-width: 3px;
        }
        .sal-kpi-label { font-size: 9.5px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: rgb(148, 163, 184); }
        .sal-kpi-value { font-size: 16px; font-weight: 800; font-family: 'JetBrains Mono', monospace; line-height: 1.1; }
        .sal-kpi-sub { font-size: 9.5px; color: rgb(100, 116, 139); margin-top: 2px; }
        .sal-input {
          width: 100%; background: rgba(15, 23, 42, 0.8);
          border: 1px solid rgb(51, 65, 85);
          border-radius: 8px; padding: 8px 12px;
          color: white; font-size: 13px;
        }
        .sal-input:focus { outline: none; border-color: rgb(16, 185, 129); }
        .sal-label { font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: rgb(148, 163, 184); margin-bottom: 4px; display: block; }
        .sal-section-title { display:flex; align-items:center; gap:6px; font-size: 10.5px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: rgb(16, 185, 129); padding-bottom: 4px; margin-bottom: 8px; border-bottom: 1px dashed rgba(16, 185, 129, 0.25); }
        .sal-icon-btn {
          width: 32px; height: 32px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(30, 41, 59, 0.6);
          border: 1px solid rgba(71, 85, 105, 0.4);
          color: rgb(203, 213, 225);
          transition: background-color 140ms, color 140ms;
          flex-shrink: 0;
        }
        .sal-icon-btn:hover { background: rgba(51, 65, 85, 0.9); color: white; }
        .sal-card {
          padding: 12px 14px; border-radius: 12px;
          background: rgba(30, 41, 59, 0.55);
          border: 1px solid rgba(71, 85, 105, 0.4);
          transition: background 140ms, border-color 140ms;
        }
        .sal-card:hover { background: rgba(51, 65, 85, 0.75); border-color: rgba(16, 185, 129, 0.3); }
        .sal-card.archived { opacity: 0.55; }
        .sal-badge {
          display: inline-flex; align-items: center; gap: 3px;
          padding: 1px 6px; border-radius: 4px;
          font-size: 9.5px; font-weight: 800; letter-spacing: 0.05em;
          text-transform: uppercase;
        }
        .sal-check {
          display: flex; align-items: center; gap: 6px;
          padding: 8px 10px; border-radius: 8px;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(71, 85, 105, 0.4);
          cursor: pointer; user-select: none;
          transition: border-color 140ms, background 140ms;
        }
        .sal-check.on { border-color: rgba(16, 185, 129, 0.6); background: rgba(16, 185, 129, 0.08); }
      `}</style>

      {/* SUB-TABS : Personnel permanent vs Stagiaires & Non permanents */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        <button
          onClick={() => setSubTab("permanent")}
          data-testid="salaries-subtab-permanent"
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
            subTab === "permanent"
              ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/60"
              : "bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800"
          }`}
        >
          <Users className="w-3.5 h-3.5" /> Personnel permanent
        </button>
        <button
          onClick={() => setSubTab("interns")}
          data-testid="salaries-subtab-interns"
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
            subTab === "interns"
              ? "bg-indigo-500/15 text-indigo-300 ring-1 ring-indigo-400/60"
              : "bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800"
          }`}
        >
          <ClipboardList className="w-3.5 h-3.5" /> Stagiaires & Non permanents
        </button>
        <button
          onClick={() => setSubTab("payments")}
          data-testid="salaries-subtab-payments"
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
            subTab === "payments"
              ? "bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/60"
              : "bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800"
          }`}
        >
          <Wallet className="w-3.5 h-3.5" /> Paiements & échéances
        </button>
        <button
          onClick={() => setSubTab("history")}
          data-testid="salaries-subtab-history"
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
            subTab === "history"
              ? "bg-blue-500/15 text-blue-300 ring-1 ring-blue-400/60"
              : "bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800"
          }`}
        >
          <TrendingUp className="w-3.5 h-3.5" /> Historique mensuel
        </button>
        <button
          onClick={() => setSubTab("schedule")}
          data-testid="salaries-subtab-schedule"
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
            subTab === "schedule"
              ? "bg-indigo-500/15 text-indigo-300 ring-1 ring-indigo-400/60"
              : "bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800"
          }`}
        >
          <CalIcon className="w-3.5 h-3.5" /> Planning hebdo
        </button>
      </div>

      {subTab === "interns" && <InternsTable currentUser={currentUser} />}
      {subTab === "payments" && <PaymentSchedulePanel currentUser={currentUser} />}
      {subTab === "history" && <SalaryHistoryPanel />}
      {subTab === "schedule" && <WeeklyScheduleTab currentUser={currentUser} />}

      {subTab === "permanent" && (<>

      {/* HEADER Branding */}
      <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-slate-900/80 to-slate-900/40 border border-slate-700/50">
        <img src="/logo_espace_maxo.png" alt="Espace Maxo" className="h-10 w-auto object-contain flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="sal-title truncate">Gestion RH & Paie</p>
          <p className="text-slate-500 text-[11px] leading-tight">Espace Maxo · Employés · Bulletins · OHADA/Bénin</p>
        </div>
        <button
          type="button"
          onClick={() => { setForm(emptyForm()); setEditingId(null); setShowAdd((s) => !s); }}
          className="h-9 px-3 rounded-lg font-bold text-[12.5px] flex items-center gap-1.5 text-white"
          style={{ background: '#a92e2e' }}
          data-testid="salaries-add-toggle"
        >
          <UserPlus className="w-4 h-4" strokeWidth={2.6} />
          {showAdd ? "Fermer" : "Nouvel employé"}
        </button>
        <button type="button" onClick={load} disabled={loading} className="sal-icon-btn" title="Actualiser">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" strokeWidth={2.4} />}
        </button>
      </div>

      {/* SÉLECTEUR MOIS/ANNÉE pour audit des KPI */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">Période analysée :</span>
        <div className="flex items-center gap-1 bg-slate-800/60 border border-slate-700 rounded-lg px-1 py-0.5">
          <button
            type="button"
            onClick={() => { if (kpiMonth === 1) { setKpiMonth(12); setKpiYear((y) => y - 1); } else { setKpiMonth((m) => m - 1); } }}
            className="h-7 w-7 rounded flex items-center justify-center text-slate-300 hover:bg-slate-700"
            data-testid="salaries-kpi-prev-month"
          >‹</button>
          <div className="text-[12px] font-bold text-white px-2 min-w-[100px] text-center">
            {["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"][kpiMonth - 1]} {kpiYear}
          </div>
          <button
            type="button"
            onClick={() => { if (kpiMonth === 12) { setKpiMonth(1); setKpiYear((y) => y + 1); } else { setKpiMonth((m) => m + 1); } }}
            className="h-7 w-7 rounded flex items-center justify-center text-slate-300 hover:bg-slate-700"
            data-testid="salaries-kpi-next-month"
          >›</button>
        </div>
        <button
          type="button"
          onClick={() => { const n = new Date(); setKpiYear(n.getFullYear()); setKpiMonth(n.getMonth() + 1); }}
          className="text-[11px] text-slate-400 hover:text-white underline decoration-dotted"
          data-testid="salaries-kpi-current-month"
        >
          Retour au mois en cours
        </button>
        {dashboard?.payment_planned_date && (
          <div
            className="ml-auto flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-200"
            data-testid="salaries-kpi-planned-payment"
            title="Règle : les nets du mois travaillé sont payés le 10 du mois suivant"
          >
            <CalIcon className="w-3.5 h-3.5" />
            <span className="text-slate-400">Échéance de paie :</span>
            <b className="text-emerald-300 font-mono">
              {new Date(dashboard.payment_planned_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
            </b>
            <button
              type="button"
              onClick={() => setSubTab("payments")}
              className="ml-1 text-amber-300 hover:text-amber-200 underline decoration-dotted text-[10.5px]"
              data-testid="salaries-kpi-goto-payments"
            >
              Gérer →
            </button>
          </div>
        )}
      </div>

      {/* KPI DASHBOARD */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div className="sal-kpi" style={{ borderLeftColor: '#10b981' }}>
            <Users className="w-5 h-5 text-emerald-400 flex-shrink-0" strokeWidth={2.2} />
            <div className="min-w-0 flex-1">
              <p className="sal-kpi-label">Employés actifs</p>
              <p className="sal-kpi-value text-emerald-300">{dashboard.counts.active}</p>
              {dashboard.counts.new_hires_month > 0 && (
                <p className="sal-kpi-sub">+{dashboard.counts.new_hires_month} ce mois</p>
              )}
            </div>
          </div>
          <div className="sal-kpi" style={{ borderLeftColor: '#f59e0b' }}>
            <TrendingUp className="w-5 h-5 text-amber-400 flex-shrink-0" strokeWidth={2.2} />
            <div className="min-w-0 flex-1">
              <p className="sal-kpi-label">Total NET du mois</p>
              <p className="sal-kpi-value text-amber-300" data-testid="salaries-total-net">
                {fmt(dashboard.payroll.total_net_month ?? dashboard.payroll.total_monthly)}<span className="text-[11px] text-amber-400/70 ml-0.5">F</span>
              </p>
              <p className="sal-kpi-sub">
                Brut {fmt(dashboard.payroll.total_brut_month ?? dashboard.payroll.total_monthly)} F · Moy {fmt(dashboard.payroll.average_monthly)} F
              </p>
            </div>
          </div>
          <div className="sal-kpi" style={{ borderLeftColor: '#a78bfa' }}>
            <Wallet className="w-5 h-5 text-violet-400 flex-shrink-0" strokeWidth={2.2} />
            <div className="min-w-0 flex-1">
              <p className="sal-kpi-label">Bons & retenues du mois</p>
              <p className="sal-kpi-value text-violet-300" data-testid="salaries-total-deductions">
                {fmt(dashboard.deductions?.total ?? dashboard.credits_total_month)}<span className="text-[11px] text-violet-400/70 ml-0.5">F</span>
              </p>
              {dashboard.deductions && (
                <p className="sal-kpi-sub">
                  Bons {fmt((dashboard.deductions.credits_auto || 0) + (dashboard.deductions.credits_manual || 0))} · Av. {fmt(dashboard.deductions.advances)} · Ret. {fmt(dashboard.deductions.manual_deductions)}
                </p>
              )}
            </div>
          </div>
          <div className="sal-kpi" style={{ borderLeftColor: '#22c55e' }}>
            <ClipboardCheck className="w-5 h-5 text-emerald-400 flex-shrink-0" strokeWidth={2.2} />
            <div className="min-w-0 flex-1">
              <p className="sal-kpi-label">Fiches signées / payées</p>
              <p className="sal-kpi-value text-emerald-300">
                {dashboard.payslips.signed}
                <span className="text-[11px] text-slate-500 ml-1">/ {dashboard.counts.active}</span>
                {typeof dashboard.payslips.paid === "number" && (
                  <span className="text-[11px] text-amber-300 ml-2" title="Nets déjà payés">
                    · {dashboard.payslips.paid} <Wallet className="w-3 h-3 inline -mt-0.5" />
                  </span>
                )}
              </p>
              <p className="sal-kpi-sub">Mois {dashboard.period}</p>
            </div>
          </div>
          <div className="sal-kpi" style={{ borderLeftColor: '#f43f5e' }}>
            <ClipboardList className="w-5 h-5 text-rose-400 flex-shrink-0" strokeWidth={2.2} />
            <div className="min-w-0 flex-1">
              <p className="sal-kpi-label">À signer</p>
              <p className="sal-kpi-value text-rose-300">{dashboard.payslips.to_sign}</p>
              <p className="sal-kpi-sub">Bulletins</p>
            </div>
          </div>
          <div className="sal-kpi" style={{ borderLeftColor: '#0ea5e9' }}>
            <Briefcase className="w-5 h-5 text-sky-400 flex-shrink-0" strokeWidth={2.2} />
            <div className="min-w-0 flex-1">
              <p className="sal-kpi-label">Répartition contrats</p>
              <p className="sal-kpi-value text-sky-300 !text-[12px] !font-bold" style={{ fontFamily: 'inherit' }}>
                {Object.entries(dashboard.contracts).filter(([, v]) => v > 0).map(([k, v]) => `${k}·${v}`).join('  ') || '—'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* DÉTAIL PAR EMPLOYÉ — Mois en cours */}
      {dashboard?.breakdown && dashboard.breakdown.length > 0 && (
        <SalaryBreakdownTable breakdown={dashboard.breakdown} period={dashboard.period} />
      )}

      {/* TOOLBAR */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher nom / matricule / poste…"
            className="w-full h-9 pl-8 pr-3 rounded-lg bg-slate-900/70 border border-slate-700 text-slate-200 text-[12.5px] placeholder:text-slate-500"
            data-testid="salaries-search"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="h-9 px-2.5 rounded-lg bg-slate-900/70 border border-slate-700 text-slate-200 text-[12px] font-semibold"
          title="Trier par"
          data-testid="salaries-sort"
        >
          <option value="name">Tri : Nom (A-Z)</option>
          <option value="salary">Tri : Salaire ↓</option>
          <option value="seniority">Tri : Ancienneté</option>
          <option value="contract">Tri : Contrat</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 px-2.5 rounded-lg bg-slate-900/70 border border-slate-700 text-slate-200 text-[12px] font-semibold"
          title="Filtrer par statut"
          data-testid="salaries-status-filter"
        >
          <option value="active">Actifs uniquement</option>
          <option value="archived">Archivés</option>
          <option value="all">Tous</option>
        </select>
        {/* 🆕 PDF de tous les bulletins du mois (période analysée) */}
        <button
          type="button"
          onClick={printMonthlyAll}
          disabled={generatingPDF || filtered.length === 0}
          className="h-9 px-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-black text-[11.5px] flex items-center gap-1.5 disabled:opacity-50"
          title={`Tous les bulletins de ${MONTHS[kpiMonth - 1]} ${kpiYear} (${filtered.length} employé(s)) en un seul PDF`}
          data-testid="salaries-pdf-month-all"
        >
          {generatingPDF ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" strokeWidth={2.8} />}
          Bulletins {MONTHS[kpiMonth - 1].slice(0, 4)}. {kpiYear}
        </button>
        {/* 🆕 PDF Annuel global */}
        <div className="h-9 flex items-center gap-1.5 pl-2 pr-1 rounded-lg bg-emerald-950/40 border border-emerald-500/30">
          <span className="text-emerald-300 text-[10.5px] font-black uppercase tracking-wider">PDF année</span>
          <select
            value={pdfYear}
            onChange={(e) => setPdfYear(parseInt(e.target.value))}
            className="h-7 px-1.5 rounded bg-slate-900/70 border border-slate-700 text-slate-200 text-[12px] font-mono"
            data-testid="salaries-pdf-year"
          >
            {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={printAnnualGlobal}
            disabled={generatingPDF || filtered.length === 0}
            className="h-7 px-2.5 rounded bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-black text-[11.5px] flex items-center gap-1 disabled:opacity-50"
            title={`PDF annuel de ${filtered.length} employé(s)`}
            data-testid="salaries-pdf-global"
          >
            {generatingPDF ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" strokeWidth={2.8} />}
            Global
          </button>
        </div>
      </div>

      {/* FORM Add/Edit */}
      {showAdd && (
        <div className="p-4 rounded-xl bg-slate-900/70 border border-emerald-500/30 space-y-4" data-testid="salaries-form">
          <p className="text-emerald-300 font-black text-[13.5px] tracking-tight">
            {editingId ? "Modifier l'employé" : "Nouvel employé"}
          </p>

          {/* Section : Identité */}
          <div>
            <p className="sal-section-title"><IdCard className="w-3.5 h-3.5" strokeWidth={2.6} /> Identité</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="sm:col-span-2">
                <label className="sal-label">Nom complet *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sal-input" placeholder="Ex: Jean Dupont" data-testid="salaries-input-name" />
              </div>
              <div>
                <label className="sal-label">Matricule</label>
                <input value={form.matricule} onChange={(e) => setForm({ ...form, matricule: e.target.value })} className="sal-input" placeholder="Auto si vide" />
              </div>
              <div>
                <label className="sal-label">Poste</label>
                <input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} className="sal-input" placeholder="Ex: Serveur" data-testid="salaries-input-role" />
              </div>
              <div>
                <label className="sal-label">Date de naissance</label>
                <input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="sal-input" />
              </div>
              <div>
                <label className="sal-label">N° pièce (CNI/CIP)</label>
                <input value={form.cni} onChange={(e) => setForm({ ...form, cni: e.target.value })} className="sal-input" placeholder="N° CNI" />
              </div>
              <div>
                <label className="sal-label">Situation familiale</label>
                <select value={form.marital_status} onChange={(e) => setForm({ ...form, marital_status: e.target.value })} className="sal-input">
                  <option value="">—</option>
                  <option value="Célibataire">Célibataire</option>
                  <option value="Marié(e)">Marié(e)</option>
                  <option value="Divorcé(e)">Divorcé(e)</option>
                  <option value="Veuf(ve)">Veuf(ve)</option>
                </select>
              </div>
              <div>
                <label className="sal-label">Enfants à charge</label>
                <input type="number" min="0" value={form.children_count} onChange={(e) => setForm({ ...form, children_count: e.target.value })} className="sal-input" />
              </div>
            </div>
          </div>

          {/* Section : Contrat */}
          <div>
            <p className="sal-section-title"><Briefcase className="w-3.5 h-3.5" strokeWidth={2.6} /> Contrat</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="sal-label">Type de contrat</label>
                <select value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} className="sal-input">
                  {CONTRACT_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="sal-label">Date d&apos;embauche</label>
                <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="sal-input" />
              </div>
              <div>
                <label className="sal-label">Date fin contrat (CDD)</label>
                <input type="date" value={form.contract_end_date} onChange={(e) => setForm({ ...form, contract_end_date: e.target.value })} className="sal-input" />
              </div>
              <div>
                <label className="sal-label">Date de sortie</label>
                <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="sal-input" />
              </div>
              <div>
                <label className="sal-label">Jours ouvrés / mois</label>
                <input type="number" min="1" max="31" value={form.work_days_per_month} onChange={(e) => setForm({ ...form, work_days_per_month: e.target.value })} className="sal-input" />
              </div>
              <div>
                <label className="sal-label">Solde congés (jours)</label>
                <input type="number" step="0.5" value={form.leave_balance_days} onChange={(e) => setForm({ ...form, leave_balance_days: e.target.value })} className="sal-input" />
              </div>
            </div>
          </div>

          {/* Section : Contact */}
          <div>
            <p className="sal-section-title"><Phone className="w-3.5 h-3.5" strokeWidth={2.6} /> Contact</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="sal-label">Téléphone</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="sal-input" placeholder="Ex: 97 00 00 00" />
              </div>
              <div>
                <label className="sal-label">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="sal-input" placeholder="mail@exemple.com" />
              </div>
              <div>
                <label className="sal-label">Mobile Money</label>
                <input value={form.mobile_money} onChange={(e) => setForm({ ...form, mobile_money: e.target.value })} className="sal-input" placeholder="Numéro MM" />
              </div>
              <div className="sm:col-span-3">
                <label className="sal-label">Adresse</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="sal-input" placeholder="Adresse physique" />
              </div>
            </div>
          </div>

          {/* Section : Paie */}
          <div>
            <p className="sal-section-title" style={{ color: '#f59e0b', borderColor: 'rgba(245,158,11,0.25)' }}><Wallet className="w-3.5 h-3.5" strokeWidth={2.6} /> Paie</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="sal-label">Salaire mensuel brut (F) *</label>
                <input type="number" value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })} className="sal-input" placeholder="0" data-testid="salaries-input-amount" />
              </div>
              <div>
                <label className="sal-label">Prime de transport / mois (F)</label>
                <input type="number" value={form.transport_allowance} onChange={(e) => setForm({ ...form, transport_allowance: e.target.value })} className="sal-input" placeholder="0" />
              </div>
            </div>
          </div>

          {/* Section : Fiscal & Social */}
          <div>
            <p className="sal-section-title" style={{ color: '#38bdf8', borderColor: 'rgba(56,189,248,0.25)' }}><Landmark className="w-3.5 h-3.5" strokeWidth={2.6} /> Fiscal & Social (OHADA/Bénin)</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="sm:col-span-1">
                <label className="sal-label">N° CNSS</label>
                <input value={form.cnss_number} onChange={(e) => setForm({ ...form, cnss_number: e.target.value })} className="sal-input" placeholder="Ex: 123456" />
              </div>
              <label className={`sal-check ${form.subject_to_cnss ? 'on' : ''}`} data-testid="salaries-subject-cnss">
                <input
                  type="checkbox"
                  checked={!!form.subject_to_cnss}
                  onChange={(e) => setForm({ ...form, subject_to_cnss: e.target.checked })}
                  className="w-4 h-4 accent-emerald-500"
                />
                <div className="min-w-0">
                  <p className="text-slate-200 font-bold text-[12px] leading-tight">Soumis à la CNSS</p>
                  <p className="text-slate-500 text-[10px] leading-tight">3.6 % du brut (plafond 500 000 F)</p>
                </div>
              </label>
              <label className={`sal-check ${form.subject_to_its ? 'on' : ''}`} data-testid="salaries-subject-its">
                <input
                  type="checkbox"
                  checked={!!form.subject_to_its}
                  onChange={(e) => setForm({ ...form, subject_to_its: e.target.checked })}
                  className="w-4 h-4 accent-emerald-500"
                />
                <div className="min-w-0">
                  <p className="text-slate-200 font-bold text-[12px] leading-tight">Soumis à l&apos;ITS</p>
                  <p className="text-slate-500 text-[10px] leading-tight">Barème progressif Bénin</p>
                </div>
              </label>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="sal-label">Notes internes</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sal-input" placeholder="Commentaire libre" />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 justify-end pt-1">
            <button type="button" onClick={() => { setShowAdd(false); setEditingId(null); setForm(emptyForm()); }} className="h-9 px-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-[12.5px] font-semibold">
              Annuler
            </button>
            <button type="button" onClick={submit} className="h-9 px-4 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold text-[12.5px] flex items-center gap-1.5" data-testid="salaries-submit">
              <Check className="w-4 h-4" strokeWidth={2.6} />
              {editingId ? "Enregistrer" : "Ajouter l'employé"}
            </button>
          </div>
        </div>
      )}

      {/* LISTE EMPLOYÉS */}
      <div>
        <div className="flex items-center gap-2 mb-2 px-1">
          <p className="text-[10.5px] uppercase tracking-[0.13em] font-bold text-emerald-400">
            {statusFilter === "archived" ? "Archives" : statusFilter === "all" ? "Tous les employés" : "Employés actifs"} ({filtered.length})
          </p>
          {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
        </div>
        {filtered.length === 0 && !loading && (
          <p className="text-slate-500 text-[12.5px] italic px-1 py-6 text-center rounded-lg bg-slate-900/40 border border-dashed border-slate-800/60">
            {search ? "Aucun résultat pour votre recherche." : "Aucun employé — cliquez sur « Nouvel employé » pour commencer."}
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {filtered.map((row) => (
            <div key={row.id} className={`sal-card ${!row.active ? 'archived' : ''}`} data-testid={`salary-row-${row.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${row.active ? 'bg-emerald-500/15 border border-emerald-500/30' : 'bg-slate-700/40'}`}>
                  <span className={`font-black text-[15px] ${row.active ? 'text-emerald-300' : 'text-slate-400'}`}>
                    {(row.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-white font-bold text-[14px] tracking-tight leading-tight truncate">{row.name}</p>
                    {row.contract_type && (
                      <span className="sal-badge bg-sky-500/15 text-sky-300 border border-sky-500/30">{row.contract_type}</span>
                    )}
                    {row.subject_to_cnss && (
                      <span className="sal-badge bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" title="Soumis à la CNSS">CNSS</span>
                    )}
                    {row.subject_to_its && (
                      <span className="sal-badge bg-amber-500/15 text-amber-300 border border-amber-500/30" title="Soumis à l'ITS">ITS</span>
                    )}
                  </div>
                  <p className="text-slate-400 text-[11.5px] leading-tight truncate">
                    {row.role_title || "—"}
                    {row.matricule && <span className="text-slate-600"> · {row.matricule}</span>}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <p className="text-amber-300 font-mono font-bold text-[13.5px] whitespace-nowrap">
                      {fmt(row.monthly_amount)}<span className="text-[10px] text-amber-400/70 ml-0.5">F</span>
                    </p>
                    {row.transport_allowance > 0 && (
                      <span className="text-[10px] text-slate-500">+ transport {fmt(row.transport_allowance)}F</span>
                    )}
                    {seniority(row.start_date) && (
                      <span className="text-[10px] text-slate-500">· ancien. {seniority(row.start_date)}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-800/50">
                <button type="button" onClick={() => setPayslipFor(row.id)} className="sal-icon-btn hover:!bg-emerald-500/20 hover:!text-emerald-300" title="Fiche de paie du mois" data-testid={`salary-payslip-${row.id}`}>
                  <FileText className="w-3.5 h-3.5" strokeWidth={2.4} />
                </button>
                <button type="button" onClick={() => printAnnualPerEmployee(row)} disabled={generatingPDF} className="sal-icon-btn hover:!bg-sky-500/20 hover:!text-sky-300 disabled:opacity-50" title={`PDF annuel ${pdfYear}`} data-testid={`salary-annual-pdf-${row.id}`}>
                  {generatingPDF ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" strokeWidth={2.4} />}
                </button>
                <button type="button" onClick={() => startEdit(row)} className="sal-icon-btn" title="Modifier" data-testid={`salary-edit-${row.id}`}>
                  <Edit2 className="w-3.5 h-3.5" strokeWidth={2.4} />
                </button>
                <button type="button" onClick={() => toggleActive(row)} className="sal-icon-btn" title={row.active ? "Archiver" : "Réactiver"}>
                  {row.active ? <X className="w-3.5 h-3.5" strokeWidth={2.4} /> : <Check className="w-3.5 h-3.5" strokeWidth={2.4} />}
                </button>
                <div className="flex-1" />
                {row.phone && (
                  <a href={`tel:${row.phone}`} className="sal-icon-btn hover:!bg-sky-500/20 hover:!text-sky-300" title={row.phone}>
                    <Phone className="w-3.5 h-3.5" strokeWidth={2.4} />
                  </a>
                )}
                <button type="button" onClick={() => removeItem(row)} className="sal-icon-btn hover:!bg-rose-500/30 hover:!text-rose-300" title="Supprimer" data-testid={`salary-delete-${row.id}`}>
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={2.4} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modale fiche de paie */}
      <PayslipModal
        open={!!payslipFor}
        onClose={() => { setPayslipFor(null); load(); }}
        salaryId={payslipFor}
        currentUser={currentUser}
      />

      </>)}
    </div>
  );
}
